import React, { useState, useContext, useEffect, useMemo } from "react";
import DetailHeader from "../components/DetailHeader";
import AuditHistory from "../components/AuditHistory";
import RoleForm from "./RoleForm";
import { getLicensedRoleTabs } from "../roleUtils";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import "./RoleDetail.css";

export default function RoleDetail({ role, onBack, onUpdate }) {
  const { companies, licenses, fetchLicenses } = useContext(
    FieldConfigurationContext
  );
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("time");

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const moduleTabs = useMemo(() => getLicensedRoleTabs(licenses), [licenses]);
  useEffect(() => {
    if (moduleTabs.length && !moduleTabs.some((t) => t.key === activeTab)) {
      setActiveTab(moduleTabs[0].key);
    }
  }, [moduleTabs, activeTab]);
  const isTabShown = (key) => moduleTabs.some((t) => t.key === key);
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);

  const payTypes = [
    { key: "overtime", label: "Overtime", code: "O" },
    { key: "double", label: "Double", code: "D" },
    { key: "sick", label: "Sick", code: "S" },
    { key: "vacation", label: "Vacation", code: "V" },
    { key: "holiday", label: "Holiday", code: "H" },
    { key: "bonus", label: "Bonus", code: "B" },
    { key: "other", label: "Other", code: "OT" },
    { key: "nontax", label: "Nontax", code: "NT" },
  ];

  // Handle edit mode toggle
  const handleEdit = () => {
    setIsEditing(!isEditing);
  };

  // Handle save from form
  const handleSave = (updatedRole) => {
    setIsEditing(false);
    onUpdate && onUpdate(updatedRole);
    setAuditRefreshKey((k) => k + 1);
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false);
  };

  // If editing, show the form
  if (isEditing) {
    return <RoleForm role={role} onSave={handleSave} onCancel={handleCancel} />;
  }

  // Otherwise show read-only detail view
  return (
    <div className="role-detail">
      <DetailHeader
        title={role.name}
        onBack={onBack}
        onEdit={handleEdit}
        isEditing={false}
        backLabel="Back to Roles"
        editLabel="Edit Role"
      />

      <div className="role-detail__content">
        {/* Basic Role Information */}
        <div className="role-detail__section role-detail__section--info">
          <h3 className="role-detail__section-title">Role Information</h3>
          <div className="role-detail__fields">
            <div className="role-detail__field">
              <label className="role-detail__label">Role Name</label>
              <div className="role-detail__value">{role.name}</div>
            </div>
            <div className="role-detail__field">
              <label className="role-detail__label">Description</label>
              <div className="role-detail__value">
                {role.description || "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Company Assignments */}
        {Object.keys(companies.configureRolesData || {}).length > 0 && (
          <div className="role-detail__section">
            <h3 className="role-detail__section-title">Company Assignments</h3>
            <div className="role-detail__company-badges">
              {Object.entries(companies.configureRolesData).map(
                ([uuid, name]) => {
                  const isAssigned = role.companyUuids?.includes(uuid);
                  const code = companies.codesMap?.[uuid];
                  return (
                    <span
                      key={uuid}
                      className={`role-detail__company-badge${
                        isAssigned
                          ? " role-detail__company-badge--assigned"
                          : ""
                      }`}
                    >
                      {code != null ? `${name} (${code})` : name}
                    </span>
                  );
                }
              )}
            </div>
          </div>
        )}

        {/* General Settings */}
        <div className="role-detail__section">
          <h3 className="role-detail__section-title">General Settings</h3>
          <div className="role-detail__checkbox-display">
            <span
              className={`role-detail__checkbox-icon ${
                role.enableJobLocationEditing ? "checked" : ""
              }`}
            >
              {role.enableJobLocationEditing ? "☑" : "☐"}
            </span>
            <span className="role-detail__checkbox-label">
              Enable Job Location Editing
            </span>
          </div>
          <div className="role-detail__checkbox-display">
            <span
              className={`role-detail__checkbox-icon ${
                role.enableAdministration ? "checked" : ""
              }`}
            >
              {role.enableAdministration ? "☑" : "☐"}
            </span>
            <span className="role-detail__checkbox-label">
              Enable Administration
            </span>
          </div>
          {role.enableAdministration && (
            <div className="role-detail__sub-permissions">
              <div className="role-detail__sub-permissions-label">
                Field Administrator
              </div>
              {[
                { key: "adminViewLicenses", label: "View Licenses" },
                {
                  key: "adminConfigureAccessGroups",
                  label: "Configure Access Groups",
                },
                {
                  key: "adminConfigureApprovalGroups",
                  label: "Configure Approval Teams",
                },
                { key: "adminConfigureRoles", label: "Configure Roles" },
                { key: "adminConfigureUsers", label: "Configure Users" },
                { key: "adminManageAch", label: "Manage ACH Information" },
              ].map(({ key, label }) => (
                <div
                  key={key}
                  className="role-detail__checkbox-display role-detail__checkbox-display--indented"
                >
                  <span
                    className={`role-detail__checkbox-icon ${
                      role[key] ? "checked" : ""
                    }`}
                  >
                    {role[key] ? "☑" : "☐"}
                  </span>
                  <span className="role-detail__checkbox-label">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Module Configuration Tabs */}
        <div className="role-detail__tabs">
          <div className="role-detail__tabs-nav">
            {moduleTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`role-detail__tab ${
                  activeTab === tab.key ? "role-detail__tab--active" : ""
                }`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="role-detail__tabs-content">
            {/* Time Entry Tab */}
            {isTabShown("time") && activeTab === "time" && (
              <div className="role-detail__tab-panel">
                <div className="role-detail__tab-header">
                  <h3 className="role-detail__tab-title">
                    Time Entry Configuration
                  </h3>
                  <div className="role-detail__checkbox-display role-detail__checkbox-display--large">
                    <span
                      className={`role-detail__checkbox-icon ${
                        role.enableTime ? "checked" : ""
                      }`}
                    >
                      {role.enableTime ? "☑" : "☐"}
                    </span>
                    <span className="role-detail__checkbox-label">
                      Enable Time
                    </span>
                  </div>
                </div>

                {role.enableTime && (
                  <div className="role-detail__settings-list">
                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.allowMultipleEmployeeEntries ? "checked" : ""
                        }`}
                      >
                        {role.allowMultipleEmployeeEntries ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Allow Multiple Employee Entries (Required for Crew
                        Entry)
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display role-detail__checkbox-display--indent-1">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.allowSigningOtherEmployees ? "checked" : ""
                        }`}
                      >
                        {role.allowSigningOtherEmployees ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Allow Signing Other Employee Entries
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display role-detail__checkbox-display--indent-1">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.showMyEmployeesWeeklySummary ? "checked" : ""
                        }`}
                      >
                        {role.showMyEmployeesWeeklySummary ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Show "My Employees'" Weekly Summary
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display role-detail__checkbox-display--indent-1">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.showMyEmployeesWorkerClass ? "checked" : ""
                        }`}
                      >
                        {role.showMyEmployeesWorkerClass ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Show "My Employees'" Worker Class
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.allowApprovals ? "checked" : ""
                        }`}
                      >
                        {role.allowApprovals ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Allow Approvals
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.allowCrewSetup ? "checked" : ""
                        }`}
                      >
                        {role.allowCrewSetup ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Allow Crew Setup
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.allowAbsenceEntries ? "checked" : ""
                        }`}
                      >
                        {role.allowAbsenceEntries ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Allow Absence Entries
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display role-detail__checkbox-display--indent-1">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.viewAndSignOnly ? "checked" : ""
                        }`}
                      >
                        {role.viewAndSignOnly ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        View and Sign Only
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.showPayType ? "checked" : ""
                        }`}
                      >
                        {role.showPayType ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Show Pay Type
                      </span>
                    </div>

                    {role.showPayType && role.payTypeOptions && (
                      <div className="role-detail__pay-types">
                        <div className="role-detail__pay-types-label">
                          Allow Regular time entries and
                        </div>
                        <div className="role-detail__pay-types-list">
                          {payTypes
                            .filter(
                              (payType) => role.payTypeOptions[payType.key]
                            )
                            .map((payType) => (
                              <span
                                key={payType.key}
                                className="role-detail__pay-type-badge"
                              >
                                {payType.label}
                              </span>
                            ))}
                          {Object.values(role.payTypeOptions).every(
                            (v) => !v
                          ) && (
                            <span className="role-detail__empty-note">
                              None selected
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.requireTimer ? "checked" : ""
                        }`}
                      >
                        {role.requireTimer ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Require Timer
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.requireJob ? "checked" : ""
                        }`}
                      >
                        {role.requireJob ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Require Job
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.showWorkersClass ? "checked" : ""
                        }`}
                      >
                        {role.showWorkersClass ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Show Workers Class
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.showDepartment ? "checked" : ""
                        }`}
                      >
                        {role.showDepartment ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Show Department
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.showWorkLocation ? "checked" : ""
                        }`}
                      >
                        {role.showWorkLocation ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Show Work Location
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.showEquipment ? "checked" : ""
                        }`}
                      >
                        {role.showEquipment ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Show Equipment
                      </span>
                    </div>

                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.showUnits ? "checked" : ""
                        }`}
                      >
                        {role.showUnits ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Show Units
                      </span>
                    </div>

                    <div className="role-detail__alert-tolerance">
                      <h4 className="role-detail__subsection-title">
                        Alert Tolerance
                      </h4>
                      <div className="role-detail__alert-values">
                        <div className="role-detail__alert-item">
                          <span className="role-detail__alert-label">
                            Timer Entry Alerts:
                          </span>
                          <span className="role-detail__alert-value">
                            {role.timerAlertMinutes || 0} Minutes
                          </span>
                        </div>
                        <div className="role-detail__alert-item">
                          <span className="role-detail__alert-label">
                            Hours Entry Alerts:
                          </span>
                          <span className="role-detail__alert-value">
                            {role.hoursAlertHours || 0} Hours
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Expense Tab */}
            {isTabShown("expense") && activeTab === "expense" && (
              <div className="role-detail__tab-panel">
                <div className="role-detail__tab-header">
                  <h3 className="role-detail__tab-title">
                    Expense Configuration
                  </h3>
                  <div className="role-detail__checkbox-display role-detail__checkbox-display--large">
                    <span
                      className={`role-detail__checkbox-icon ${
                        role.enableExpense ? "checked" : ""
                      }`}
                    >
                      {role.enableExpense ? "☑" : "☐"}
                    </span>
                    <span className="role-detail__checkbox-label">
                      Enable Expense
                    </span>
                  </div>
                </div>

                {role.enableExpense && (
                  <div className="role-detail__placeholder">
                    <p>
                      No additional expense configuration options are currently
                      available.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Project Management Tab */}
            {isTabShown("project") && activeTab === "project" && (
              <div className="role-detail__tab-panel">
                <div className="role-detail__tab-header">
                  <h3 className="role-detail__tab-title">
                    Project Management Configuration
                  </h3>
                  <div className="role-detail__checkbox-display role-detail__checkbox-display--large">
                    <span
                      className={`role-detail__checkbox-icon ${
                        role.enableProjectManagement ? "checked" : ""
                      }`}
                    >
                      {role.enableProjectManagement ? "☑" : "☐"}
                    </span>
                    <span className="role-detail__checkbox-label">
                      Enable Project Management
                    </span>
                  </div>
                </div>

                {role.enableProjectManagement && (
                  <div className="role-detail__settings-list">
                    <div className="role-detail__checkbox-display">
                      <span
                        className={`role-detail__checkbox-icon ${
                          role.allowFieldLogs ? "checked" : ""
                        }`}
                      >
                        {role.allowFieldLogs ? "☑" : "☐"}
                      </span>
                      <span className="role-detail__checkbox-label">
                        Allow Field Logs
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Service Management Tab */}
            {isTabShown("service") && activeTab === "service" && (
              <div className="role-detail__tab-panel">
                <div className="role-detail__tab-header">
                  <h3 className="role-detail__tab-title">
                    Service Management Configuration
                  </h3>
                  <div className="role-detail__checkbox-display role-detail__checkbox-display--large">
                    <span
                      className={`role-detail__checkbox-icon ${
                        role.enableServiceManagement ? "checked" : ""
                      }`}
                    >
                      {role.enableServiceManagement ? "☑" : "☐"}
                    </span>
                    <span className="role-detail__checkbox-label">
                      Enable Service Management
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <AuditHistory
          resource="roles"
          uuid={role.uuid}
          refreshKey={auditRefreshKey}
          createdAt={role.created_at}
        />
      </div>
    </div>
  );
}
